import { getPositionForDriverEventFromWatch } from "../../../helpers/position";
import api, { browserSessionId, noContentDecoder } from "../../api";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { addFileReferenceWithTrailerT, deleteFileReferenceT } from "./types";
import { DateTime } from "luxon";
import { apiFetch } from "../../../helpers/apiRequest";
import { FileTransfer } from "@capacitor/file-transfer";
import { App } from "@capacitor/app";
import { isRetryableUploadError } from "../../../helpers/uploadRetry";
import axios from "axios";

const prefix = "/app/file-uploads";

export const addFileReference = createErrorReportingAsyncThunk(
  `${prefix}/add-file-reference`,
  async (
    input: {
      cargoId: string;
      truckId: string;
      stopId: string;
      file: {
        documentType: "POD" | "IMAGE";
        name: string;
        mimeType: string;
        s3Identifier: string;
      };
    },
    thunkApi
  ) => {
    const position = await getPositionForDriverEventFromWatch();
    await api(thunkApi).post({
      url: `/drivers-api/files/file-reference`,
      body: addFileReferenceWithTrailerT.encode({
        ...input,
        trailerId: input.truckId,
        time: DateTime.now().toISO(),
        position,
      }),
      decoder: noContentDecoder,
    });
  }
);

interface FileUploadURLResponse {
  uploadUrl: string;
  s3Identifier: string;
}

type UploadContentType = "image/jpeg" | "application/pdf";

const requestFileUploadURL = async (
  contentType: UploadContentType
): Promise<FileUploadURLResponse> => {
  const result = await apiFetch(`/drivers-api/files/file-upload-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ contentType }),
  });

  if (!result.ok) {
    throw new Error("Failed to create upload URL");
  }

  const data: FileUploadURLResponse = await result.json();
  return data;
};

export const createFileUploadURL = createErrorReportingAsyncThunk<
  FileUploadURLResponse,
  {
    contentType?: UploadContentType;
  }
>(`${prefix}/create-file-upload-url`, (input) =>
  requestFileUploadURL(input.contentType || "image/jpeg")
);

const waitUntilAppActive = async () => {
  if ((await App.getState()).isActive) return;
  await new Promise<void>((resolve) => {
    const listener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void listener.then((l) => l.remove());
        resolve();
      }
    });
  });
};

/**
 * Uploads a file from the device to S3 and returns its s3Identifier. Gets its
 * own presigned URL so a retry can get a fresh one: the URL is only valid for
 * 15 minutes, and the app can be suspended for longer than that mid-upload.
 */
export const uploadFileToS3Mobile = createErrorReportingAsyncThunk(
  `${prefix}/upload-file-to-s3`,
  async (input: { contentType: UploadContentType; path: string }) => {
    const upload = async () => {
      const { uploadUrl, s3Identifier } = await requestFileUploadURL(
        input.contentType
      );
      await FileTransfer.uploadFile({
        url: uploadUrl,
        path: input.path,
        headers: { "Content-Type": input.contentType },
        method: "PUT",
      });
      return s3Identifier;
    };
    try {
      return await upload();
    } catch (e) {
      // A transfer the app was suspended during (phone locked, app switched)
      // fails, or runs with an expired URL, once the app is back in the
      // foreground. Retry once while active, with a fresh URL.
      if (!isRetryableUploadError(e)) throw e;
      await waitUntilAppActive();
      return await upload();
    }
  }
);

export const deleteFileReference = createErrorReportingAsyncThunk(
  `${prefix}/delete-file-reference`,
  async (input: { fileId: string; truckId: string }) => {
    const position = await getPositionForDriverEventFromWatch();

    const response = await apiFetch(`/drivers-api/files/${input.fileId}`, {
      method: "DELETE",
      headers: {
        "content-type": "application/json",
        browserSessionId,
      },
      body: JSON.stringify(
        deleteFileReferenceT.encode({
          trailerId: input.truckId,
          time: DateTime.now().toISO(),
          position,
        })
      ),
    });

    if (!response.ok) {
      throw new Error("Failed to delete file");
    }
  }
);

export const uploadFileToS3Web = createErrorReportingAsyncThunk(
  `${prefix}/upload-file-to-s3-web`,
  async (input: { uploadUrl: string; contentType: string; file: File }) => {
    const buffer = await input.file.arrayBuffer();
    await axios.put(input.uploadUrl, buffer, {
      headers: { "Content-Type": input.contentType },
    });
  }
);
